import { useState, useEffect } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { Input } from "../components/Input";
import { loadSession, saveSession } from "../auth";
import { updateMyProfile } from "../services/api";
import { useTranslation } from "react-i18next";

export default function Profile() {
  const { t } = useTranslation();
  const session = loadSession() || {};
  const sessionUser = session.user || {};
  const initialEmail = sessionUser.email || "";
  const initialName = sessionUser.name || "";

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [currentPasswordConfirm, setCurrentPasswordConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(initialEmail);
    setName(initialName);
  }, [initialEmail, initialName]);

  function mapProfileError(message) {
    const key = `profile.error_${message}`;
    const translated = t(key);
    return translated === key ? message || t("profile.save_failed", "Failed to save profile") : translated;
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    const nextEmail = email.trim().toLowerCase();
    const nextName = name.trim();
    const patch = {};

    if (nextEmail !== initialEmail) patch.email = nextEmail;
    if (nextName !== initialName) patch.name = nextName || null;

    if (!Object.keys(patch).length) {
      alert(t("profile.no_changes", "No changes to save"));
      return;
    }

    if ((currentPassword || currentPasswordConfirm) && currentPassword !== currentPasswordConfirm) {
      alert(t("profile.password_mismatch", "Passwords do not match"));
      return;
    }

    setSaving(true);
    try {
      const res = await updateMyProfile(patch);
      if (res?.user) {
        saveSession({
          ...session,
          user: res.user,
        });
      }
      setCurrentPassword("");
      setCurrentPasswordConfirm("");
      alert(t("profile.saved", "Profile saved"));
    } catch (err) {
      alert(mapProfileError(err?.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-5">{t("profile.title", "User Profile")}</h1>
      <Card>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={handleSave}>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 font-medium mb-1">{t("profile.email", "Email")}</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 font-medium mb-1">{t("profile.name", "Name")}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs text-slate-500 font-medium mb-1">{t("profile.current_password", "Current password")}</label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs text-slate-500 font-medium mb-1">{t("profile.current_password_confirm", "Retype current password")}</label>
            <Input type="password" value={currentPasswordConfirm} onChange={(e) => setCurrentPasswordConfirm(e.target.value)} />
          </div>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving}>{saving ? t("profile.saving", "Saving...") : t("profile.save", "Save")}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
